import {
  SpaceBetween,
  FormField,
  DatePicker,
  Select,
  Button,
  Input,
} from '@cloudscape-design/components';
import { useEffect, useMemo, useState } from 'react';
import { CurrencyRate } from '../common/types';

export type DashboardFilterProps = {
  date: string;
  setDate: (date: string) => void;
  days: number;
  setDays: (days: number) => void;
  regionCurrency: string | undefined;
  setRegionCurrency: (currency: string | undefined) => void;
  baseCurrency: string | undefined | null;
  setBaseCurrency: (currency: string | undefined) => void;
  convertedCurrency: string | undefined | null;
  setConvertedCurrency: (currency: string | undefined) => void;
  convertedCurrencyRate: CurrencyRate | undefined;
  refresh: () => void;
  loading: boolean;
};

const options = [
  { label: '7 Days', value: '7' },
  { label: '30 Days', value: '30' },
  { label: '60 Days', value: '60' },
  { label: '90 Days', value: '90' },
];

const regionCurrencyOptions = [
  { label: 'NeedZappy CA', value: 'CAD' },
  { label: 'NeedZappy USA', value: 'USD' },
  { label: 'NeedZappy Global', value: undefined },
];

const baseCurrencyOptions = [
  { label: 'CAD', value: 'CAD' },
  { label: 'USD', value: 'USD' },
];

const convertedCurrencyOptions = [
  { label: 'USD', value: 'USD' },
  { label: 'CAD', value: 'CAD' },
];

export function DashboardFilter(props: DashboardFilterProps) {
  const {
    date,
    setDate,
    days,
    setDays,
    refresh,
    loading,
    regionCurrency,
    setRegionCurrency,
    baseCurrency,
    setBaseCurrency,
    convertedCurrency,
    setConvertedCurrency,
    convertedCurrencyRate,
  } = props;
  const [localDate, setLocalDate] = useState(date);

  useEffect(() => {
    setLocalDate(date);
  }, [date]);

  const currentCurrencyOption = useMemo(() => {
    // if there's a region currency, this current currency will either be region currency if converted isnt set, or converted currency if it is set
    if (regionCurrency) {
      if (convertedCurrency) {
        return (
          convertedCurrencyOptions.find((option) => option.value === convertedCurrency) ||
          convertedCurrencyOptions[0]
        );
      }

      return (
        convertedCurrencyOptions.find((option) => option.value === regionCurrency) ||
        convertedCurrencyOptions[0]
      );
    }
 
      // if no region currency, this has to be the converted currency
      return (
        convertedCurrencyOptions.find((option) => option.value === convertedCurrency) ||
        convertedCurrencyOptions[0]
      );
    
  }, [convertedCurrency, regionCurrency]);

  const conversionRate = useMemo(() => {
    if (convertedCurrencyRate) {
      return `1 ${
        convertedCurrencyRate.base_currency
      } = ${convertedCurrencyRate.conversion_rate.toFixed(4)} ${
        convertedCurrencyRate.target_currency
      }`;
    }

    return '-';
  }, [convertedCurrencyRate]);

  return (
    <SpaceBetween direction="horizontal" size="s">
      <FormField constraintText="Use YYYY/MM/DD format." label="Reporting Date">
        <DatePicker
          disabled={loading}
          locale="en-US"
          openCalendarAriaLabel={(selectedDate) =>
            'Choose reporting date' + (selectedDate ? `, selected date is ${selectedDate}` : '')
          }
          placeholder="YYYY/MM/DD"
          value={localDate}
          expandToViewport
          onChange={({ detail }) => {
            const newDate = detail.value;
            setLocalDate(newDate);

            // Need to check that the date is actually in YYYY-MM-DD format
            if (newDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
              setDate(newDate);
            }
          }}
        />
      </FormField>
      <FormField
        constraintText="Number of days to look back."
        label={days === 1 ? `Last ${days} day` : `Last ${days} days`}
      >
        <Select
          disabled={loading}
          options={options}
          selectedOption={options.find((option) => option.value === days.toString()) || options[0]}
          expandToViewport
          onChange={({ detail }) =>
            setDays(Math.max(parseInt(detail.selectedOption.value ?? '30'), 1))
          }
        />
      </FormField>
      <FormField label="Business Entity" constraintText="Business entity for reporting.">
        <Select
          disabled={loading}
          options={regionCurrencyOptions}
          selectedOption={
            regionCurrencyOptions.find((option) => option.value === regionCurrency) ||
            regionCurrencyOptions[0]
          }
          expandToViewport
          onChange={({ detail }) => {
            const selected = detail.selectedOption.value;

            if (!selected) {
              // switching to global
              // default to USD as the currency and we are converting to CAD
              setRegionCurrency(undefined);
              // from CAD to USD by default when switching to global
              setBaseCurrency('CAD');
              setConvertedCurrency('USD');
            } else {
              // remove conversions and go back to region currency
              setRegionCurrency(selected);
              setBaseCurrency(undefined);
              setConvertedCurrency(undefined);
            }
          }}
        />
      </FormField>
      <FormField label="Currency" constraintText="Reporting currency.">
        <Select
          disabled={loading}
          options={convertedCurrencyOptions}
          selectedOption={currentCurrencyOption}
          expandToViewport
          onChange={({ detail }) => {
            const selected = detail.selectedOption.value ?? 'USD';

            if (regionCurrency) {
              if (selected !== regionCurrency) {
                // if the selected currency is different from the region currency, we have to do a conversion
                setConvertedCurrency(selected);
                // if there's a region currency, the base currency will always be the region currency.
                setBaseCurrency(regionCurrency);
              } else {
                // unnecessary to set either of these
                setConvertedCurrency(undefined);
                setBaseCurrency(undefined);
              }
            } else {
              // if there's no region currency, the base currency will always be the opposite of the selected currency
              setBaseCurrency(selected === 'USD' ? 'CAD' : 'USD');
              setConvertedCurrency(selected);
            }
          }}
        />
      </FormField>
      <FormField label="Conversion Rate" constraintText="Conversion rate for reporting currency.">
        <Input readOnly disabled={loading} value={conversionRate} />
      </FormField>
      <FormField label="Refresh">
        <Button disabled={loading} iconName="refresh" onClick={refresh} />
      </FormField>
    </SpaceBetween>
  );
}
